import { useWrongQuestions } from "@/hooks/useLearning";
import { useDueReviews } from "@/hooks/useQuestionBank";
import { Link } from "react-router-dom";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

export default function YanlisSorularim() {
  const { data, loading, error, reload, resolveQuestion } = useWrongQuestions();
  const { data: dueReviews } = useDueReviews();

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const visible = data ?? [];
  const reviewLink = (lesson: string, topic: string) => {
    const params = new URLSearchParams({ lesson, topic, count: "10", duration: "15" });
    return `/ogrenci/soru-bankasi/test?${params.toString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Yanlış Sorularım</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Yanlış yaptığın soruları tekrar edip pekiştir.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold whitespace-nowrap">
          <i className="ri-close-circle-line" aria-hidden="true"></i>
          {visible.length} yanlış soru
        </span>
      </div>

      {/* Aralıklı tekrar: tekrar zamanı gelen sorular */}
      {dueReviews && dueReviews.length > 0 && (
        <section className="rounded-xl border border-secondary-200 bg-secondary-50 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2">
              <i className="ri-refresh-line text-secondary-600" aria-hidden="true"></i>
              Tekrar Zamanı Gelen Sorular
            </h2>
            <span className="text-xs font-semibold text-secondary-800 bg-secondary-100 px-2.5 py-1 rounded-full whitespace-nowrap">
              {dueReviews.length} soru
            </span>
          </div>
          <p className="text-xs text-foreground-600 mb-4">
            Aralıklı tekrar yöntemiyle, unutmadan önce yeniden çözmen gereken sorular.
          </p>
          <div className="space-y-3">
            {dueReviews.map((q) => (
              <div key={q.questionId} className="bg-background-50 rounded-lg border border-background-200 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                    {q.lessonName}
                  </span>
                  <span className="text-xs font-semibold text-secondary-800 bg-secondary-100 px-2 py-0.5 rounded-full">
                    {q.topicName}
                  </span>
                  <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">
                    {q.attemptCount} deneme
                  </span>
                </div>
                <p className="text-sm text-foreground-900 leading-relaxed">{q.questionText}</p>
                <Link
                  to={reviewLink(q.lessonName, q.topicName)}
                  className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-secondary-500 text-background-50 text-xs font-semibold hover:bg-secondary-600"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  Tekrar Testini Başlat
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="space-y-4">
        {visible.map((q) => (
          <div key={q.id} className="bg-background-50 rounded-xl border border-background-200 p-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                {q.subject}
              </span>
              <span className="text-xs font-semibold text-secondary-800 bg-secondary-100 px-2 py-0.5 rounded-full">
                {q.topic}
              </span>
              <span className="text-xs text-foreground-400 ml-auto">{q.date}</span>
            </div>

            <p className="text-sm md:text-base text-foreground-900 font-medium leading-relaxed">{q.question}</p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                <p className="text-xs font-semibold text-red-600 mb-1">Senin Cevabın</p>
                <p className="text-sm text-foreground-800">{q.myAnswer}</p>
              </div>
              <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-3">
                <p className="text-xs font-semibold text-secondary-800 mb-1">Doğru Cevap</p>
                <p className="text-sm text-foreground-900 font-medium">{q.correctAnswer}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Link
                to={reviewLink(q.subject, q.topic)}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-primary-300 bg-primary-50 text-primary-800 text-sm font-semibold hover:bg-primary-100 whitespace-nowrap"
              >
                <i className="ri-book-open-line" aria-hidden="true"></i>
                Konuyu Tekrar Çalış
              </Link>
              <button
                type="button"
                onClick={() => resolveQuestion(q.id)}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-check-line" aria-hidden="true"></i>
                Listeden Kaldır
              </button>
            </div>
          </div>
        ))}

        {visible.length === 0 && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mx-auto">
              <i className="ri-check-double-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-heading font-semibold text-base text-foreground-700">
              Harika, yanlış sorun kalmadı!
            </p>
            <p className="mt-1.5 text-sm text-foreground-500">
              Yeni soru çözdükçe yanlışların burada birikecek.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
